<script>
import flatpickr from 'flatpickr'
import axios from "axios";

function checkServerError(response) {
  if (response.status !== 200) {
    throw new Error(response.statusText);
  }

  return response;
}

export default {
  mixins: [
    require('../../../spark/mixins/stripe')
  ],
  props: [],
  mounted() {
    this.cardElement = this.createCardElement('#stripe-card-element', {})

    this.datePicker = document.querySelector('#gift-datepicker')
    flatpickr(this.datePicker, { defaultDate: new Date() })
  },
  computed: {
    selectedPlanAmountInCents() {
      const x = this.giftee.plan.split(' ')[2] // "($24.99)"
      const stripped = x.replace(/\(|\)|\$/g, '') // "24.99"
      const inCents = Number(stripped) * 100 // 2499
      return inCents
    },
    showCouponNotice() {
      return new Date() < new Date('2023-01-01')
    }
  },
  data() {
    return {
      acceptTerms: false,
      submitted: false,
      loading: false,
      giftee: {
        name: '',
        email: '',
        plan: '12 months ($199.99)',
      },
      gifter: {
        name: '',
        email: '',
        zipcode: ''
      },
      coupon: '',
    }
  },
  methods: {
    async sendInvoice({ coupon }) {
      try {
        const giftAmount = (coupon.amount_off)
          ? (this.selectedPlanAmountInCents - coupon.amount_off)
          : (coupon.percent_off)
            ? (this.selectedPlanAmountInCents - this.selectedPlanAmountInCents * (coupon.percent_off / 100))
            : this.selectedPlanAmountInCents

        const response = await this.serverRequest('/gift/send-invoice', {
          body: {
            gifterName: this.gifter.name,
            gifterEmail: this.gifter.email,
            giftAmount,
            giftDate: this.datePicker.value,
            giftPlan: this.giftee.plan.split(' (')[0],
            gifteeName: this.giftee.name,
            gifteeEmail: this.giftee.email,
          }
        })

        if (response.error) throw new Error(response.error)
      } catch (err) {
        console.error(err)
        throw new Error('Sorry, your gift was processed successfully but there was a problem sending your invoice, please contact customer support at contact@thebutterbook.com for your receipt')
      }
    },

    async serverRequest(url, config = {}) {
      const csrfToken = window.Spark.csrfToken

      return axios({
        method: config.method || "post",
        url,
        data: config.body,
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-TOKEN": csrfToken,
        },
      })
        .then(checkServerError)
        .then((x) => x.data);
    },

    async setupPaymentMethod() {
      try {
        const response = await this.stripe.createPaymentMethod({
          type: 'card',
          card: this.cardElement,
          billing_details: {
            name: this.gifter.name,
            email: this.gifter.email
          }
        })

        if (response.error) {
          throw new Error(response.error)
        }

        return response.paymentMethod.id
      } catch (err) {
        console.error(err)
        throw new Error('Sorry, there was a problem with your request. Please review your payment method and try again.')
      }
    },

    async setupUser() {
      const { name: giftee, email, plan } = this.giftee
      const gifterName = this.gifter.name
      const gifterEmail = this.gifter.email
      try {
        const planLengthMonths = Number(plan.split(' ')[0])
        const response = await this.serverRequest('/gift/setup-pending-user', {
          body: {
            giftee,
            email,
            planLengthMonths,
            giftDate: this.datePicker.value,
            gifterName,
            gifterEmail,
          }
        })

        if (response.error) throw new Error(response.error)

        return response
      } catch (err) {
        console.error(err)

        if (err.message.includes('Duplicate entry')) throw new Error('User already has an account, they cannot be gifted a subscription at this time')

        throw new Error('Trouble creating the new user, payment cancelled ... please try again. : ' + err.message)
      }
    },

    async createSubscription({ customerId, paymentMethodId, giftPlan, userId, trialEndsAt, coupon }) {
      try {
        const response = await this.serverRequest('/create-subscription', {
          method: 'POST',
          body: {
            customerId,
            paymentMethodId,
            giftPlan,
            coupon: coupon.id,
            userId,
            trialEndsAt
          }
        })

        if (response.error) throw new Error(response.error)

      } catch (err) {
        console.error(err)
        throw new Error('Sorry there was a problem creating the gift subscription, please contact support at contact@thebutterbook.com for help')
      }
    },

    async setupSubscriptionDependencies() {
      let userResponse
      let paymentMethodId
      try {
        userResponse = await this.setupUser()
        paymentMethodId = await this.setupPaymentMethod()

        return {
          userResponse,
          paymentMethodId
        }
      } catch (error) {
        console.error(error)
        await this.revertUser({ userResponse })

        return {
          userResponse,
          paymentMethodId,
          error: error.message
        }
      }
    },

    async revertUser({ userResponse }) {
      try {
        const sqlUserId = userResponse.user.id
        const stripeUserId = userResponse.stripe.id
        await this.serverRequest('/revert-user', { body: { sqlUserId, stripeUserId } })
      } catch (err) {
        console.error(err)
      }
    },

    async submitGift() {
      try {
        this.validateInputs()

        this.loading = true

        const coupon = await this.getCoupon()
        if (!coupon.valid) throw new Error('Sorry but that coupon is no longer valid, please remove it and try again')

        const { userResponse, paymentMethodId, error } = await this.setupSubscriptionDependencies()
        if (!userResponse) throw new Error('Sorry but there was a problem creating the user, please try again or contact support at contact@thebutterbook.com for help. Full error details: ' + error)
        if (!paymentMethodId) throw new Error('Sorry but there was a problem with the payment method, please try again or contact support at contact@thebutterbook.com for help')

        await this.createSubscription({
          customerId: userResponse.stripe.id,
          paymentMethodId,
          giftPlan: this.giftee.plan,
          userId: userResponse.user.id,
          trialEndsAt: userResponse.user.trial_ends_at,
          coupon
        })

        await this.sendInvoice({ coupon })

        this.loading = false
        this.submitted = true

        let planPrice = 75;
        if (this.giftee.plan == '6 months ($150.00)') {
          planPrice = 150;
        }
        else if (this.giftee.plan == '12 months ($199.99)') {
          planPrice = 199.99;
        }
        if (typeof fbq === 'function') {
          fbq('track', 'Purchase', { value: planPrice, currency: 'USD' })
        }
        if (process.env.MIX_GTAG_ID && typeof gtag === 'function') {
          gtag('event', 'conversion', {
            'send_to': `${process.env.MIX_GTAG_ID}/qY9oCL7ntcwBEO-G05oD`,
            'value': planPrice,
            'currency': 'USD'
          })
        }
        window.scrollTo(0, 0)
      } catch (err) {
        alert(err.message)

        this.loading = false
      }
    },

    async getCoupon() {
      if (!this.coupon) return { valid: true }

      try {
        let couponCode = this.coupon
        if (couponCode.toUpperCase().trim() === 'KABMOMSDAY21') {
          couponCode = 'KABMOMSDAY21FIX'
        }
        const response = await this.serverRequest(`/coupons/${couponCode}`, { method: 'GET' })

        if (response.error) {
          console.error(response.error);
          throw new Error('Coupon does not exist')
        }

        return response.coupon
      } catch (err) {
        console.error(err)
        if (err.message === 'Coupon does not exist') throw err

        throw new Error('Sorry there was a problem creating the gift subscription, please contact support at contact@thebutterbook.com for help')
      }
    },

    validateInputs() {
      const { name: giftee, email: gifteeEmail, plan } = this.giftee
      const { name: gifter, zipcode, email: gifterEmail } = this.gifter

      if (!giftee) throw new Error('Please provide the giftee\'s name')
      if (!gifteeEmail) throw new Error('Please provide the email for the giftee')
      if (!plan) throw new Error('Please select a plan to be gifted')
      if (!gifter) throw new Error('Please provide your name')
      if (!gifterEmail) throw new Error('Please provide your email to send the receipt')
      if (!zipcode) throw new Error('Please provide the zipcode for your card')
      if (!this.datePicker.value) throw new Error('Please select a date for the gift to be received')

      if (!this.acceptTerms) throw new Error('Please accept the terms and conditions')
    },
  }
}
</script>

